(() => {
  if (window.__mastryAsShownLock) return;
  window.__mastryAsShownLock = true;

  const PICKER_SELECTOR = 'variant-radios, variant-selects';
  const AS_SHOWN = (value) => typeof value === 'string' && value.startsWith('As Shown');
  const NORMAL_DEFAULTS = { Frame: 'Wood', Hanger: 'Z-Bar' };

  function getData(picker) {
    if (picker.__asShownVariantData) return picker.__asShownVariantData;
    const node = picker.querySelector('script[type="application/json"]');
    if (!node) return [];
    try {
      picker.__asShownVariantData = JSON.parse(node.textContent);
    } catch (error) {
      picker.__asShownVariantData = [];
      console.error('As Shown lock could not read variant data.', error);
    }
    return picker.__asShownVariantData;
  }

  function optionNames(picker) {
    return Array.from(picker.querySelectorAll('.product-form__input')).map((group) => {
      const control = group.querySelector('input[type="radio"], select');
      return control ? control.name.replace(/^options\[|\]$/g, '') : '';
    });
  }

  function selectedValues(picker) {
    return Array.from(picker.querySelectorAll('.product-form__input')).map((group) => {
      const select = group.querySelector('select');
      if (select) return select.value;
      const radio = group.querySelector('input[type="radio"]:checked');
      return radio ? radio.value : '';
    });
  }

  function valuesByName(picker) {
    const names = optionNames(picker);
    const values = selectedValues(picker);
    return Object.fromEntries(names.map((name, index) => [name, values[index]]));
  }

  function variantOptions(variant, names) {
    if (Array.isArray(variant.options)) return Object.fromEntries(names.map((name, index) => [name, variant.options[index]]));
    return { Size: variant.option1, Frame: variant.option2, Hanger: variant.option3 };
  }

  function variantById(variants, id) {
    return variants.find((variant) => String(variant.id) === String(id));
  }

  function currentVariantFromUrl(variants) {
    const id = new URL(window.location.href).searchParams.get('variant');
    return id ? variantById(variants, id) : null;
  }

  function isAsShownVariant(variant, names) {
    if (!variant) return false;
    const options = variantOptions(variant, names);
    if (!AS_SHOWN(options.Frame)) return false;
    return !names.includes('Hanger') || AS_SHOWN(options.Hanger);
  }

  function normalSizes(variants, names) {
    return [...new Set(
      variants
        .filter((variant) => !isAsShownVariant(variant, names))
        .map((variant) => variantOptions(variant, names).Size)
        .filter(Boolean)
    )];
  }

  function findExact(variants, names, wanted) {
    return variants.find((variant) => {
      const options = variantOptions(variant, names);
      return names.every((name) => options[name] === wanted[name]) && variant.available !== false;
    });
  }

  function applyVariant(picker, variant, sourceControl) {
    const names = optionNames(picker);
    const options = variantOptions(variant, names);
    let changed = false;

    names.forEach((name) => {
      const value = options[name];
      const optionName = `options[${name}]`;
      const controls = Array.from(
        picker.querySelectorAll('input[type="radio"], select')
      ).filter((control) => control.name === optionName || control.name === name);
      const select = controls.find((control) => control.tagName === 'SELECT');
      if (select) {
        if (select.value !== value) {
          select.value = value;
          changed = true;
        }
        return;
      }
      const radio = controls.find((control) => control.value === value);
      if (radio && !radio.checked) {
        radio.checked = true;
        changed = true;
      }
    });

    const dispatchControl =
      sourceControl && sourceControl.closest(PICKER_SELECTOR) === picker
        ? sourceControl
        : picker.querySelector('input[type="radio"]:checked, select');

    if (dispatchControl && changed) {
      const event = new Event('change', { bubbles: true });
      Object.defineProperty(event, '__mastryAsShownSynthetic', { value: true });
      dispatchControl.dispatchEvent(event);
    }
  }

  function chooseTarget(picker, sourceControl) {
    const variants = getData(picker);
    const names = optionNames(picker);
    if (!variants.length || !names.includes('Frame')) return null;

    const asShownVariants = variants.filter((variant) => isAsShownVariant(variant, names));
    if (!asShownVariants.length) return null;

    const selected = valuesByName(picker);
    const name = sourceControl.name.replace(/^options\[|\]$/g, '');
    const value = sourceControl.value;
    const previous = currentVariantFromUrl(variants);

    if (name === 'Frame' && AS_SHOWN(value)) {
      return asShownVariants.find((variant) => variantOptions(variant, names).Frame === value) || asShownVariants[0];
    }

    if (name === 'Hanger' && AS_SHOWN(value)) {
      return (
        asShownVariants.find((variant) => variantOptions(variant, names).Frame === selected.Frame) ||
        asShownVariants[0]
      );
    }

    if (name === 'Size') {
      const matches = variants.filter((variant) => variantOptions(variant, names).Size === value);
      if (matches.length && matches.every((variant) => isAsShownVariant(variant, names))) {
        return matches[0];
      }
    }

    if (isAsShownVariant(previous, names) && !AS_SHOWN(value)) {
      const sizes = normalSizes(variants, names);
      const middleSize = sizes[Math.floor(sizes.length / 2)] || sizes[0];
      const wanted = {
        Size: name === 'Size' ? value : middleSize,
        Frame: name === 'Frame' ? value : NORMAL_DEFAULTS.Frame,
        Hanger: name === 'Hanger' ? value : NORMAL_DEFAULTS.Hanger
      };
      const exact = findExact(variants, names, wanted);
      if (exact) return exact;

      const normal = variants.filter((variant) => !isAsShownVariant(variant, names) && variant.available !== false);
      return (
        normal.find((variant) => variantOptions(variant, names)[name] === value) ||
        normal[0] ||
        null
      );
    }

    return null;
  }

  document.addEventListener(
    'change',
    (event) => {
      if (event.__mastryAsShownSynthetic) return;
      const control = event.target.closest('input[type="radio"], select');
      if (!control) return;
      const picker = control.closest(PICKER_SELECTOR);
      if (!picker) return;
      const target = chooseTarget(picker, control);
      if (!target) return;

      const names = optionNames(picker);
      const targetOptions = variantOptions(target, names);
      const current = valuesByName(picker);
      const needsCorrection = names.some((name) => current[name] !== targetOptions[name]);
      if (!needsCorrection) return;

      event.stopImmediatePropagation();
      applyVariant(picker, target, control);
    },
    true
  );

  function syncPicker(picker) {
    if (!picker || picker.dataset.asShownLockReady === 'true') return;
    picker.dataset.asShownLockReady = 'true';
    const variants = getData(picker);
    const names = optionNames(picker);
    const requested = currentVariantFromUrl(variants);
    if (isAsShownVariant(requested, names)) applyVariant(picker, requested, null);
  }

  function syncAll(root = document) {
    if (root.matches && root.matches(PICKER_SELECTOR)) syncPicker(root);
    root.querySelectorAll?.(PICKER_SELECTOR).forEach(syncPicker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => syncAll(), { once: true });
  } else {
    syncAll();
  }

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) syncAll(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
